import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { agents } from "@/lib/mockData";
import {
  clearChat,
  forwardChatToBoardroom,
  getChat,
  listChats,
  sendChatMessage,
} from "@/lib/chatApi";
import AgentList from "./AgentList";
import ChatHeader from "./ChatHeader";
import MessageBubble from "./MessageBubble";
import Composer from "./Composer";
import ErrorInline from "@/components/common/ErrorInline";
import { cn } from "@/lib/utils";

/**
 * AgentsPage — real LLM-backed 1:1 advisor chat (Phase C).
 *
 * Threads persist per (user_id, agent_id) on the server. We keep a local
 * `previews` map keyed by agent_id (driving the left rail) and a `messages`
 * array for the active thread.
 */
export default function AgentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryId = searchParams.get("id");

  const initialId =
    queryId && agents.some((a) => a.id === queryId) ? queryId : agents[0].id;

  const [activeId, setActiveId] = useState(initialId);
  // previews: { [agent_id]: { last_message_preview, updated_at, unread_for_user } }
  const [previews, setPreviews] = useState(() =>
    Object.fromEntries(
      agents.map((a) => [
        a.id,
        { last_message_preview: null, updated_at: null, unread_for_user: false },
      ]),
    ),
  );
  // messages for the currently-loaded thread
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [typing, setTyping] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const scrollRef = useRef(null);

  // ---- URL <-> state ---------------------------------------------------
  useEffect(() => {
    if (queryId !== activeId) {
      setSearchParams({ id: activeId }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (queryId && queryId !== activeId && agents.some((a) => a.id === queryId)) {
      setActiveId(queryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId]);

  const activeAgent = useMemo(
    () => agents.find((a) => a.id === activeId) ?? agents[0],
    [activeId],
  );

  // ---- Initial load: previews ------------------------------------------
  const refreshPreviews = useCallback(async () => {
    try {
      const list = await listChats();
      setPreviews((prev) => {
        const next = { ...prev };
        for (const row of list || []) {
          next[row.agent_id] = {
            last_message_preview: row.last_message_preview,
            updated_at: row.updated_at,
            unread_for_user: !!row.unread_for_user,
          };
        }
        return next;
      });
    } catch (err) {
      // Swallow on first paint; surface only if user-initiated.
      // eslint-disable-next-line no-console
      console.warn("[chats] preview load failed", err?.message);
    }
  }, []);

  useEffect(() => {
    refreshPreviews();
  }, [refreshPreviews]);

  // ---- Load active thread on activeId change ---------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setThreadLoading(true);
      setThreadError(null);
      setMessages([]);
      try {
        const detail = await getChat(activeId);
        if (cancelled) return;
        setMessages(detail?.messages || []);
        // Mark unread cleared locally too
        setPreviews((prev) => ({
          ...prev,
          [activeId]: { ...(prev[activeId] || {}), unread_for_user: false },
        }));
      } catch (err) {
        if (!cancelled) {
          setThreadError(err?.response?.data?.detail || err.message || "Couldn't load chat");
        }
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  // ---- Auto-scroll -----------------------------------------------------
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages.length, typing, activeId]);

  // ---- Send ------------------------------------------------------------
  const handleSend = async (text) => {
    if (!text.trim() || typing) return;
    // Optimistic: append a tentative user bubble immediately so input clears
    // and the user sees their message instantly. We'll reconcile with the
    // server-issued message_id when the response lands.
    const tempId = `tmp-${Date.now()}`;
    const tempUserMsg = {
      message_id: tempId,
      thread_id: "",
      user_id: "",
      sender: "user",
      msg_type: "text",
      content: text.trim(),
      sequence: messages.length,
      created_at: new Date().toISOString(),
      __temp: true,
    };
    setMessages((prev) => [...prev, tempUserMsg]);
    setTyping(true);
    try {
      const { user_message, agent_message } = await sendChatMessage(
        activeId,
        text.trim(),
      );
      setMessages((prev) =>
        prev.map((m) => (m.message_id === tempId ? user_message : m)).concat(agent_message),
      );
      // Refresh sidebar previews
      refreshPreviews();
    } catch (err) {
      // Rollback the optimistic user msg on failure
      setMessages((prev) => prev.filter((m) => m.message_id !== tempId));
      toast.error("Message failed", {
        description: err?.response?.data?.detail || err.message || "Try again.",
      });
    } finally {
      setTyping(false);
    }
  };

  // ---- Clear thread ----------------------------------------------------
  const handleClear = async () => {
    if (!messages.length) return;
    try {
      await clearChat(activeId);
      setMessages([]);
      setPreviews((prev) => ({
        ...prev,
        [activeId]: { last_message_preview: null, updated_at: null, unread_for_user: false },
      }));
      toast.success("Conversation cleared");
    } catch (err) {
      toast.error("Couldn't clear", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  // ---- Forward to Boardroom -------------------------------------------
  const handleForward = async () => {
    if (forwarding) return;
    const lastUserMsg = [...messages].reverse().find((m) => m.sender === "user");
    if (!lastUserMsg) {
      toast.message("Nothing to forward yet", {
        description: "Send a message first, then we'll seed the boardroom.",
      });
      return;
    }
    setForwarding(true);
    try {
      const sess = await forwardChatToBoardroom(activeId, lastUserMsg.content);
      toast.success("Forwarded to Boardroom · Debate started", {
        description: `${activeAgent.name}'s thread is seeded into the executive boardroom.`,
      });
      navigate(`/boardroom?debate=${sess.session_id}`);
    } catch (err) {
      toast.error("Forward failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setForwarding(false);
    }
  };

  const showChat = !!queryId; // mobile: only show chat if an agent is selected

  return (
    <div data-testid="agents-page" className="flex h-full min-w-0 flex-1">
      <div
        className={cn(
          "h-full w-full md:flex md:w-[300px] md:flex-shrink-0",
          showChat ? "hidden md:flex" : "flex",
        )}
      >
        <AgentList
          previews={previews}
          activeId={activeAgent.id}
          onSelect={setActiveId}
        />
      </div>
      <section
        className={cn(
          "min-w-0 flex-1 flex-col bg-[#F7F7F5] md:flex",
          showChat ? "flex" : "hidden md:flex",
        )}
      >
        <ChatHeader
          agent={activeAgent}
          onForward={handleForward}
          forwarding={forwarding}
          onClear={handleClear}
          canClear={messages.length > 0}
          onBack={() => setSearchParams({}, { replace: true })}
        />
        <div
          ref={scrollRef}
          data-testid="agents-chat-scroll"
          className="idea-scroll flex-1 space-y-4 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8 lg:px-12"
        >
          {threadLoading && messages.length === 0 ? (
            <div
              data-testid="agents-chat-loading"
              className="text-center text-[12px] text-zinc-400"
            >
              Loading conversation…
            </div>
          ) : threadError ? (
            <ErrorInline
              message={threadError}
              onRetry={() => setActiveId((x) => x)}
            />
          ) : messages.length === 0 ? (
            <EmptyState agent={activeAgent} />
          ) : (
            messages.map((m, i) => (
              <MessageBubble
                key={m.message_id}
                msg={m}
                agent={activeAgent}
                index={i}
              />
            ))
          )}
          {typing && <TypingIndicator agent={activeAgent} />}
        </div>
        <div className="border-t border-black/[0.04] bg-[#F7F7F5] px-5 py-3 sm:px-8 sm:py-4 lg:px-12">
          <Composer agent={activeAgent} onSend={handleSend} disabled={typing} />
        </div>
      </section>
    </div>
  );
}

function EmptyState({ agent }) {
  return (
    <div
      data-testid="agents-empty-state"
      className="flex h-full flex-col items-center justify-center gap-2 text-center"
    >
      <div className="text-[14px] font-bold text-zinc-700">
        Speak with {agent.name} ({agent.role})
      </div>
      <div className="max-w-md text-[12.5px] leading-relaxed text-zinc-500">
        Ask anything in their scope — {agent.focus}. They'll reply with
        advice, a recommendation, or a warning based on the substance.
      </div>
    </div>
  );
}

function TypingIndicator({ agent }) {
  return (
    <div data-testid="typing-indicator" className="flex items-start gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-black/[0.04] bg-white text-[12px] font-extrabold text-zinc-800">
        {agent.name[0]}
      </div>
      <div className="rounded-2xl border border-black/[0.04] bg-white px-4 py-3">
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-400"
              style={{
                animation: `idea-dot-pulse 1.1s ease-in-out infinite`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
